/*
 * NHOI — More to discover
 * Continues a collection page with products from the source collection
 * (Shop All, best-selling order) once the current collection runs out.
 *
 * Rules:
 *  - only starts after the main grid has no pages left
 *  - never shows a product already rendered on the page
 *  - stays off while a filter or a sort is active
 *  - stays off on the source collection's own page
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nhoi-more]';
  var MAIN_GRID = '#ProductGridContainer';
  var TRIGGER_MARGIN = 400;

  function handleFromHref(href) {
    var match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? match[1] : null;
  }

  function NhoiMore(root) {
    this.root = root;
    this.grid = root.querySelector('[data-nhoi-more-grid]');
    this.head = root.querySelector('[data-nhoi-more-head]');
    this.status = root.querySelector('[data-nhoi-more-status]');
    this.sentinel = root.querySelector('[data-nhoi-more-sentinel]');
    this.sectionId = root.dataset.sectionId;
    this.sourceUrl = root.dataset.sourceUrl;
    this.isSource = root.dataset.isSource === 'true';

    this.generation = 0;
    this.failed = false;
    this.error = root.querySelector('[data-nhoi-more-error]');
    this.retry = root.querySelector('[data-nhoi-more-retry]');
    this.page = 1;
    this.loading = false;
    this.done = false;
    this.observer = null;
    this.scrollBound = null;
    this.seen = Object.create(null);
    this.seenCards = Object.create(null);

    this.init();
  }

  NhoiMore.prototype.init = function () {
    if (this.isSource) return; // nothing left to continue with
    if (!this.grid || !this.sentinel || !this.sourceUrl || !this.sectionId) return;

    this.collectSeen();
    this.observe();

    var self = this;
    if (this.retry) this.retry.addEventListener('click', function () {
      self.failed = false;
      if (self.error) self.error.hidden = true;
      self.maybeLoad();
    });
    // The theme fires this after filters / sorting re-render the grid.
    document.addEventListener('collection:rerendered', function () {
      self.reset();
    });
  };

  // Any product already on the page must never show up again below.
  NhoiMore.prototype.collectSeen = function () {
    var self = this;
    var links = document.querySelectorAll(MAIN_GRID + ' a[href*="/products/"]');
    Array.prototype.forEach.call(links, function (link) {
      var handle = handleFromHref(link.getAttribute('href'));
      if (handle) self.seen[handle] = true;
    });
  };

  NhoiMore.prototype.isBlocked = function () {
    var params = new URLSearchParams(window.location.search);
    var blocked = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) blocked = true;
    });
    return blocked || params.has('sort_by') || params.has('q');
  };

  // True once the main collection grid has nothing left to load.
  NhoiMore.prototype.mainGridExhausted = function () {
    return !document.querySelector(MAIN_GRID + ' .pagination button[action], ' + MAIN_GRID + ' .pagination .pagination__item--next');
  };

  NhoiMore.prototype.sentinelInRange = function () {
    var rect = this.sentinel.getBoundingClientRect();
    return rect.top <= (window.innerHeight || 0) + TRIGGER_MARGIN;
  };

  NhoiMore.prototype.observe = function () {
    var self = this;

    if (!this.observer && typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) self.maybeLoad();
          });
        },
        { rootMargin: TRIGGER_MARGIN + 'px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    // Fallback for cases where the observer never fires (zero-height or
    // display-suppressed sentinel, older browsers).
    if (!this.scrollBound) {
      var ticking = false;
      this.scrollBound = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          if (self.sentinelInRange()) self.maybeLoad();
        });
      };
      window.addEventListener('scroll', this.scrollBound, { passive: true });
      window.addEventListener('resize', this.scrollBound, { passive: true });
    }

    // The page may already be scrolled past the trigger point.
    window.requestAnimationFrame(function () {
      if (self.sentinelInRange()) self.maybeLoad();
    });
  };

  NhoiMore.prototype.stopObserving = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.scrollBound) {
      window.removeEventListener('scroll', this.scrollBound);
      window.removeEventListener('resize', this.scrollBound);
      this.scrollBound = null;
    }
  };

  NhoiMore.prototype.maybeLoad = function () {
    if (this.loading || this.done || this.failed || !this.root.isConnected) return;
    if (this.isBlocked()) return;
    if (!this.mainGridExhausted()) return;
    this.load();
  };

  NhoiMore.prototype.fetchBatch = function (page) {
    var url = new URL(this.sourceUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('page', String(page));

    return fetch(url.toString())
      .then(function (response) {
        if (!response.ok) throw new Error('Bad response');
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var source = doc.querySelector('[data-nhoi-more-source]');
        if (!source) throw new Error('No batch in response');
        return {
          cards: source.querySelectorAll('.card'),
          hasNext: source.dataset.hasNext === 'true',
        };
      });
  };

  NhoiMore.prototype.load = function () {
    var self = this;
    var generation = this.generation;
    this.collectSeen();
    this.loading = true;
    if (this.error) this.error.hidden = true;
    if (this.status) this.status.hidden = false;

    // A batch can be entirely made of products the shopper already saw, so
    // keep pulling pages until something new turns up (or the source ends).
    var step = function (added) {
      if (generation !== self.generation || self.done || added > 0) return Promise.resolve(added);

      return self
        .fetchBatch(self.page)
        .then(function (batch) {
          if (generation !== self.generation || !self.root.isConnected) return 0;
          if (self.isBlocked()) return 0;
          self.collectSeen();
          self.page += 1;
          if (!batch.hasNext) self.done = true;

          Array.prototype.forEach.call(batch.cards, function (card) {
            var link = card.querySelector('a[href*="/products/"]');
            var handle = card.dataset.productHandle || handleFromHref(link && link.getAttribute('href'));
            var key = card.dataset.nhoiCardKey || handle;
            if (!handle || self.seen[handle] || self.seenCards[key]) return;
            self.seenCards[key] = true;
            self.grid.appendChild(card);
            added += 1;
          });

          return step(added);
        })
        .catch(function () {
          if (generation === self.generation) {
            self.failed = true;
            if (self.error) self.error.hidden = false;
          }
          return added;
        });
    };

    return step(0).then(function (added) {
      if (generation !== self.generation) return;
      self.loading = false;
      if (self.status) self.status.hidden = true;

      if (added > 0) {
        if (self.head) self.head.hidden = false;
        if (typeof self.grid.reload === 'function') self.grid.reload();
        document.dispatchEvent(new CustomEvent('nhoi:more-loaded', { detail: { added: added } }));
      }

      if (self.done) {
        self.stopObserving();
      } else {
        // The new rows may not have pushed the sentinel out of range.
        window.requestAnimationFrame(function () {
          if (self.sentinelInRange()) self.maybeLoad();
        });
      }
    });
  };

  // Filters or sorting changed: throw the continuation away and start over.
  NhoiMore.prototype.reset = function () {
    this.generation += 1;
    this.failed = false;
    if (this.error) this.error.hidden = true;
    this.grid.innerHTML = '';
    if (this.head) this.head.hidden = true;
    if (this.status) this.status.hidden = true;
    this.page = 1;
    this.done = false;
    this.loading = false;
    this.seen = Object.create(null);
    this.seenCards = Object.create(null);
    this.collectSeen();
    this.observe();
  };

  function mount(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.nhoiMoreMounted === 'true') return;
      root.dataset.nhoiMoreMounted = 'true';
      new NhoiMore(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mount(document);
    });
  } else {
    mount(document);
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });
})();
